import { ReactNode, useEffect, useState } from 'react'
import { IntercomProvider, useIntercom } from 'react-use-intercom'
import { getIntercomUserJwt } from 'utils'

declare global {
  interface Window {
    Intercom?: (command: string, ...args: any[]) => void
  }
}

export const DEFAULT_INTERCOM_APP_ID = 'uiwmuutv'

export interface IntercomUserLike {
  email: string
  id: string
  name?: string
}

export interface AppIntercomProviderProps {
  appId?: string
  children: ReactNode
  user: IntercomUserLike | null | undefined
}

export default function AppIntercomProvider({
  appId = DEFAULT_INTERCOM_APP_ID,
  children,
  user,
}: AppIntercomProviderProps) {
  const { intercomAutoBootProps, intercomReady, intercomUserJwt, intercomWithoutUserId } =
    useIntercomBootState(user)

  return (
    <IntercomProvider
      appId={appId}
      autoBoot
      autoBootProps={intercomAutoBootProps}
      shouldInitialize={intercomReady}
    >
      <IntercomUserSync
        intercomUserJwt={intercomUserJwt}
        intercomWithoutUserId={intercomWithoutUserId}
        user={user}
      />
      {children}
    </IntercomProvider>
  )
}

interface Props {
  intercomUserJwt: string | null
  intercomWithoutUserId: boolean
  user: IntercomUserLike | null | undefined
}

export function IntercomUserSync({ intercomUserJwt, intercomWithoutUserId, user }: Props) {
  const { update } = useIntercom()

  useEffect(() => {
    if (user === undefined || !user) return
    if (!intercomWithoutUserId && !intercomUserJwt) return
    update(
      intercomUserJwt
        ? {
            email: user.email,
            intercomUserJwt,
            name: user.name,
          }
        : { email: user.email, name: user.name }
    )
  }, [intercomUserJwt, intercomWithoutUserId, update, user])

  return null
}

export function useIntercomBootState(user: IntercomUserLike | null | undefined) {
  const [intercomUserJwt, setIntercomUserJwt] = useState<string | null>(null)
  const [intercomWithoutUserId, setIntercomWithoutUserId] = useState(false)

  useEffect(() => {
    if (user === undefined || user === null) {
      setIntercomUserJwt(null)
      setIntercomWithoutUserId(false)
      return
    }

    let cancelled = false
    setIntercomUserJwt(null)
    setIntercomWithoutUserId(false)

    getIntercomUserJwt(user.name ? { name: user.name } : undefined)
      .then(res => {
        if (!cancelled) setIntercomUserJwt(res.data)
      })
      .catch(() => {
        if (!cancelled) setIntercomWithoutUserId(true)
      })

    return () => {
      cancelled = true
    }
  }, [user?.email, user?.id, user?.name])

  const intercomReady =
    user !== undefined && (user === null || intercomUserJwt !== null || intercomWithoutUserId)

  const intercomAutoBootProps =
    user === null
      ? undefined
      : user && intercomUserJwt
        ? { email: user.email, intercomUserJwt, name: user.name }
        : user && intercomWithoutUserId
          ? { email: user.email, name: user.name }
          : undefined

  return {
    intercomAutoBootProps,
    intercomReady,
    intercomUserJwt,
    intercomWithoutUserId,
  }
}
